<template>
  <v-card
    v-if="item && Object.keys(item).length > 0"
    flat
    class="printableArea font my-0"
    id="printBill"
    :outlined="outlined"
  >
    <v-card-text class="pa-0">

      <!-- ─── Header ─── -->
      <div class="text-center justify-content-center">
        <img
          v-if="Img && show_logo"
          width="70"
          :src="Img"
          alt="logo"
          class="logo"
        />
        <h4 class="font pt-2 text-capitalize">{{ storeName }}</h4>
        <div v-if="branch && branchName" class="font default">{{ branchName }}</div>
        <div v-if="phoneMobileLine" class="font default receipt-store-phone">
          {{ phoneMobileLine }}
        </div>
        <div v-if="show_store_address && storeAddress" class="font default receipt-store-address">
          {{ storeAddress }}
        </div>
        <h3 class="text-center font font-weight-bolder">
          {{ $t("buffet_food_order_slip") }}
        </h3>
      </div>

      <!-- ─── Meta ─── -->
      <div v-if="queueLabel" class="text-center my-2">
        <div class="font default">{{ $t("queue_number") }}</div>
        <div
          class="font font-weight-black"
          style="font-size: 28px; line-height: 1.1"
        >
          {{ queueLabel }}
        </div>
      </div>
      <div class="font default">
        {{ $t("code") }} : <span class="bold">{{ item.code || item.txn || "" }}</span>
      </div>
      <div class="font default">
        {{ $t("date") }} : {{ item.created_at | moment }}
      </div>
      <div class="font default">
        {{ $t("seller") }} :
        {{ item.user_id && item.user_id.first_name ? item.user_id.first_name : $t("unknown") }}
      </div>
      <div v-if="show_payment_method" class="font default">
        {{ $t("payment_method") }} : {{ $t(paymentMethodText) }}
      </div>

      <hr class="dot" />

      <!-- ─── Table number ─── -->
      <div class="text-center">
        <div class="font bfr-table-label">{{ $t("table_no") }}</div>
        <div class="font bfr-table-number">{{ tableName }}</div>
      </div>

      <hr class="dot" />

      <!-- ─── Buffet plans ─── -->
      <div class="font default font-weight-bold">{{ $t("buffet_plans") }}</div>
      <div
        v-for="(plan, idx) in safeBuffetPlans"
        :key="idx"
        class="printClass font default"
      >
        <div>{{ plan.persons | ToNumber }}x {{ buffetPlanName(plan) }} ({{ plan.unit_price | NumberFormat }})</div>
        <div class="font-weight-bold--text">{{ plan.subtotal | NumberFormat }}</div>
      </div>

      <!-- ─── Session times ─── -->
      <div class="printClass font default" style="margin-top:2px">
        <div>{{ $t("start_time") }}</div>
        <div class="font-weight-bold--text">{{ startTime }}</div>
      </div>
      <div class="printClass font default">
        <div>{{ $t("end_time") }}</div>
        <div class="font-weight-bold--text">{{ endTime }}</div>
      </div>

      <!-- ─── Extra food items ─── -->
      <template v-if="extraItems.length > 0">
        <hr class="dot" />
        <div class="font default font-weight-bold">{{ $t("extra_items") }}</div>
        <v-simple-table class="bill-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="font text-left">{{ $t("item") }}</th>
                <th class="font text-center">{{ $t("amount") }}</th>
                <th class="font text-right">{{ $t("price") }}</th>
                <th class="font text-right">{{ $t("total") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, i) in extraItems" :key="i">
                <td class="font text-left">{{ getProductName(line) }}</td>
                <td class="font text-center">{{ line.qty | ToNumber }}</td>
                <td class="font text-right">{{ line.price_sale | NumberFormat }}</td>
                <td class="font text-right">{{ line.total_price | NumberFormat }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div class="printClass font default">
          <div>{{ $t("subtotal") }}</div>
          <div class="font-weight-bold--text">{{ extraSubtotal | NumberFormat }}</div>
        </div>
      </template>

      <hr class="dot" />

      <!-- ─── Totals ─── -->
      <div class="printClass font default">
        <div>{{ $t("buffet_plans") }}</div>
        <div class="font-weight-bold--text">{{ buffetSubtotal | NumberFormat }}</div>
      </div>
      <div v-if="extraItems.length > 0" class="printClass font default">
        <div>{{ $t("extra_items") }}</div>
        <div class="font-weight-bold--text">{{ extraSubtotal | NumberFormat }}</div>
      </div>
      <div class="printClass font default font-weight-bold">
        <div>{{ $t("grand_total") }}</div>
        <div class="font-weight-bold--text">{{ item.total_price | NumberFormat }} {{ currency }}</div>
      </div>

      <hr class="dot" />

      <!-- ─── Footer ─── -->
      <div class="font text-center" v-html="footer_text"></div>

    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";
import { formatBillQueue } from "@/utils/billQueue";

export default {
  name: "AppBuffetReceipt",

  mixins: [myMixin],

  props: {
    item: Object,
    show_logo: { type: Boolean, default: true },
    branch: { type: Boolean, default: false },
    show_phone_number: { type: Boolean, default: false },
    show_mobile_number: { type: Boolean, default: false },
    show_store_address: { type: Boolean, default: false },
    show_payment_method: { type: Boolean, default: false },
    footer_text: String,
    outlined: { type: Boolean, default: false },
  },

  computed: {
    ...mapGetters({ user: "auth/user" }),

    queueLabel() {
      return formatBillQueue(this.item?.queue_no);
    },

    Img() {
      if (this.user?.stores?.logo && !this.user.stores.logo.includes("default")) {
        return Help.img(this.user.stores.logo);
      }
      return null;
    },

    storeName() {
      return this.item?.store_id?.name || this.user?.stores?.name || "PosSmart";
    },

    branchName() {
      return (
        this.item?.branch_id?.name ||
        this.user?.branch_id?.name ||
        null
      );
    },

    storePhone() {
      return (
        this.item?.store_id?.phone_no ||
        this.user?.stores?.phone_no ||
        null
      );
    },

    storeMobile() {
      return (
        this.item?.store_id?.mobile_no ||
        this.user?.stores?.mobile_no ||
        null
      );
    },

    storeAddress() {
      return (
        this.item?.store_id?.address ||
        this.user?.stores?.address ||
        null
      );
    },

    phoneMobileLine() {
      const parts = [];
      if (this.show_phone_number && this.storePhone) {
        parts.push(this.storePhone);
      }
      if (this.show_mobile_number && this.storeMobile) {
        parts.push(this.storeMobile);
      }
      return parts.join(" / ");
    },

    tableName() {
      const t = this.item?.table_id;
      if (t && typeof t === "object") return t.name || "-";
      if (t) return t;
      return this.item?.table_name || "-";
    },

    currency() {
      return this.item?.currency_id?.code || "LAK";
    },

    paymentMethodText() {
      try {
        const pd = this.item?.payment_data;
        const method = pd
          ? (typeof pd === "string" ? JSON.parse(pd) : pd).method
          : this.item?.type;
        return method || "cash";
      } catch {
        return this.item?.type || "cash";
      }
    },

    safeBuffetPlans() {
      const r = this.item;
      if (!r) return [];
      if (Array.isArray(r.buffet_plans) && r.buffet_plans.length) return r.buffet_plans;
      if (Array.isArray(r.buffetPlans) && r.buffetPlans.length) return r.buffetPlans;
      return [];
    },

    buffetSubtotal() {
      return this.safeBuffetPlans.reduce((s, p) => s + (p.subtotal || 0), 0);
    },

    extraItems() {
      const history = this.item?.history;
      if (!Array.isArray(history)) return [];
      return history.filter((h) => (h.total_price || 0) > 0);
    },

    extraSubtotal() {
      return this.extraItems.reduce((s, h) => s + (h.total_price || 0), 0);
    },

    startTime() {
      if (this.item?.start_time) return this.item.start_time;
      if (this.item?.created_at) {
        const d = new Date(this.item.created_at);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      }
      return "--:--";
    },

    endTime() {
      if (this.item?.end_time) return this.item.end_time;
      if (this.item?.created_at) {
        const d = new Date(this.item.created_at);
        const duration = Number(this.item?.buffet_duration) || 120;
        d.setMinutes(d.getMinutes() + duration);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      }
      return "--:--";
    },

  },

  methods: {
    buffetPlanName(plan) {
      return plan.plan_name || plan.buffet_plan_name || plan.name || "";
    },

    getProductName(line) {
      return line.product?.name || line.product_id?.name || line.name || "-";
    },
  },
};
</script>

